/**
 * 게임이 쓰는 소리의 문 하나. 화면과 전장은 여기만 부른다.
 *
 * - `initSound`: 창에 한 번 건다. 모든 누름에서 컨텍스트를 깨우고(켜 둔 사람만), 단추를 누르면 톡 소리를 낸다.
 * - `cycleSound`: 편성 화면의 소리 단추. 끔 → 작게 → 크게.
 * - `BattleSound`: 판 하나를 지켜보는 것(`BattleSoundObserver`)을 감싼다 — 틱 뒤마다 `tick`, 판이 끝나면 `end`.
 *   다시 보기에는 `end`가 없다: 기록의 끝이 이긴 판인지 진 판인지를 여기서 다시 가리지 않는다.
 */
import type { World } from '../sim/world.ts';
import { WebAudioBackend } from './backend.ts';
import { BattleMix, type Hear } from './battle-mix.ts';
import { HoofMix, HoofWatch, type Herd } from './hoof.ts';
import type { CueId } from './cues.ts';
import { AudioDirector } from './director.ts';
import { BattleSoundObserver } from './observer.ts';
import { LEVEL_GAIN, nextLevel, savedLevel, saveLevel, type SoundLevel } from './settings.ts';

const backend = new WebAudioBackend();
const director = new AudioDirector(backend, () => performance.now());
let level: SoundLevel = 0;
let wired = false;

function apply(): void {
  backend.setGain(LEVEL_GAIN[level]);
  director.muted = level === 0 || (typeof document !== 'undefined' && document.hidden);
}

/** 창에 한 번 건다. 여러 번 불러도 한 번만 걸린다. */
export function initSound(): void {
  if (wired || typeof document === 'undefined') return;
  wired = true;
  level = savedLevel();
  apply();
  // 잡는 단계(capture)에서 듣는다 — 화면이 눌림을 먹어도(stopPropagation) 소리는 난다.
  document.addEventListener(
    'click',
    (e) => {
      if (level === 0) return;
      backend.unlock();
      const t = e.target;
      // 소리 단추는 제 소리(`ui.on`)를 따로 낸다.
      if (t instanceof Element && t.closest('button, select, [role="button"]') && !t.closest('[data-sound-toggle]')) director.cue('ui.tap');
    },
    true,
  );
  document.addEventListener('visibilitychange', () => {
    backend.setHidden(document.hidden);
    apply();
  });
  // 뒤로 가기 캐시에서 되살아난 창(bfcache)은 visibilitychange 없이 돌아올 수 있다.
  window.addEventListener('pageshow', () => {
    backend.setHidden(document.hidden);
    apply();
  });
}

export function soundLevel(): SoundLevel {
  return level;
}

/** 소리 단추를 눌렀다(손짓 안에서 부른다). 새 단계를 돌려준다. 켰으면 그 크기로 한 번 들려준다. */
export function cycleSound(): SoundLevel {
  level = nextLevel(level);
  saveLevel(level);
  apply();
  if (level > 0) {
    backend.unlock();
    // 방금 만든 컨텍스트는 아직 깨어나는 중일 수 있다 — 깨어난 뒤에 낸다(곧 깨어나지 않으면 버린다).
    backend.whenRunning(() => director.cue('ui.on'));
  }
  return level;
}

/** 화면이 내는 소리(들어 보기 등). */
export function playCue(id: CueId): void {
  director.cue(id);
}

/** 판 하나의 소리. 싸우는 판과 다시 보기가 같은 것을 쓴다. */
export class BattleSound {
  private readonly observer: BattleSoundObserver;
  private readonly mix = new BattleMix();
  private readonly hoofWatch: HoofWatch;
  private readonly hoofMix = new HoofMix();
  private readonly herd: Herd = { n: 0, pace: 0, x: 0, y: 0 };
  private ended = false;

  /** 판을 열 때(되감기라면 건너뛴 뒤에) 만든다 — 그때의 상태가 기준이 된다. */
  constructor(world: World, speed = 1, flat = false) {
    director.beginSession(speed);
    this.observer = new BattleSoundObserver(world);
    // 밤에는 낱소리에 좌우를 주지 않는다 — 보이지 않는 적의 자리가 소리로 드러나지 않게.
    this.mix.flat = flat;
    // 말발굽도 같다: 밤에는 우리 말만 세고 자리를 싣지 않는다(`hoof.ts`).
    this.hoofWatch = new HoofWatch(flat);
    this.hoofMix.flat = flat;
  }

  /**
   * 틱이 하나 돈 뒤마다. 다시 보기는 그 틱을 돌린 배속을 함께 넘긴다 — 처음에는 배속을 프레임 끝에 따로 맞췄는데,
   * 배속을 바꾼 첫 프레임의 틱들은 앞의 배속으로 걸러졌다(1→8배의 첫 소리가 새고, 8→1배의 첫 소리가 빠졌다 — 리뷰).
   */
  tick(w: World, speed = 1, hear: Hear | null = null): void {
    if (this.ended) return;
    director.speed = speed;
    for (const id of this.observer.observe(w)) director.cue(id);
    // 낱소리(칼·창·화살·쓰러짐)와 깔개: 이번 틱의 셈(`World.fx`)을 묶어 낸다. `hear`는 자리를 화면의 좌우·거리로 바꾼다.
    director.grains(this.mix.step(w.fx, performance.now(), hear, speed), this.mix.bedLevel);
    // 말발굽: 달리는 말의 수와 걸음을 읽어 발굽 소리 한 겹으로.
    this.hoofWatch.read(w, this.herd);
    director.sprinkle(this.hoofMix.step(this.herd, hear, speed));
  }

  /** 판이 끝났다. 한 번만 낸다. */
  end(won: boolean): void {
    if (this.ended) return;
    this.ended = true;
    director.bedOff();
    director.cue(won ? 'win' : 'lose');
  }

  /** 판을 떠난다: 나던 소리를 끊는다. */
  close(): void {
    this.ended = true;
    director.beginSession();
  }
}
